import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
  FormControl,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  type Control,
  Controller,
  type UseFormSetValue,
} from "react-hook-form";

import useDevMode from "../../../utils/devmode";
import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import { CREDENTIAL_TYPE } from "../common";
import type { CredentialsStore } from "../store";
import { InputLabelText } from "../ui/InputLabelText";
import { InputWrapper } from "../ui/InputWrapper";
import { LineBreak } from "../ui/LineBreak";
import { SupportText } from "../ui/SelectSupportText";
import UserSignalIcon from "../ui/UserSignalIcon.svg";
import { MfaFields } from "./MfaFields";
import type { AddPayerCredentialsFormFields } from "./PayerCredentialsForm.zod";
import { UserFields } from "./UserFields";

export const ADD_PAYER_VALUE = "addNewPayer";

interface AddPayerCredentialFormFieldProps {
  control: Control<AddPayerCredentialsFormFields>;
  setValue: UseFormSetValue<AddPayerCredentialsFormFields>;
  practices: PracticeMessage[];
  sources: SourceMessage[];
  pmses: PMSMessage[];
  userCredentials: CredentialsStore["userCredentials"];
  selectedPractice: PracticeMessage["wieldyId"];
  selectedType: AddPayerCredentialsFormFields["type"];
  selectedSource?: SourceMessage["id"];
  selectedPMS?: PMSMessage["id"];
  existingMfaJson?: string | null;
  initialUserType?: AddPayerCredentialsFormFields["userType"];
  initialUserId?: AddPayerCredentialsFormFields["userId"];
  cancel: () => void;
}

export function AddPayerCredentialFormFields({
  control,
  setValue,
  practices,
  sources,
  pmses,
  userCredentials,
  selectedPractice,
  selectedType,
  selectedSource = undefined,
  selectedPMS = undefined,
  existingMfaJson = undefined,
  initialUserType = undefined,
  initialUserId = undefined,
  cancel,
}: AddPayerCredentialFormFieldProps) {
  const devMode = useDevMode();
  const hasPayers = Array.isArray(sources);
  const hasPMSes = Array.isArray(pmses);
  const placeholderSourceText = "Choose Source";
  const selectedSourceObject =
    sources && selectedType === CREDENTIAL_TYPE.PAYER
      ? sources.find((source) => source.id === selectedSource) || null
      : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        padding: "1rem 0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: ".5rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#F2F4F7",
              width: "48px",
              height: "48px",
              borderRadius: "24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "8px solid #F9FAFB",
            }}
          >
            <img
              src={UserSignalIcon}
              alt="Credentials Icon"
              width="24"
              height="24"
            />
          </Box>
          <IconButton
            aria-label="close"
            onClick={cancel}
            sx={{
              color: "#475467",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          paragraph
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#303030",
            marginBottom: 0,
          }}
        >
          Add a new login
        </Typography>
        <Typography
          paragraph
          sx={{
            fontSize: "0.875rem",
            fontWeight: 400,
            color: "#475467",
            marginBottom: 0,
          }}
        >
          Please fill the detail below to create a new login
        </Typography>
      </Box>

      <LineBreak />

      <Controller
        name="practiceId"
        control={control}
        rules={{ required: true }}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "start",
            }}
          >
            <FormLabel>
              <InputLabelText>Practice</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <Select
                name={name}
                value={value}
                label="Choose Practice"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                displayEmpty
                IconComponent={KeyboardArrowDownOutlinedIcon}
                inputProps={{
                  "data-testid": "practiceInput",
                }}
                MenuProps={{
                  sx: {
                    marginTop: "1rem",
                  },
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  borderRadius: "8px",
                  fontSize: "0.875rem",

                  ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                    {
                      padding: "12px 14px",
                    },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                  fieldset: {
                    legend: {
                      width: 0,
                    },
                  },
                }}
                renderValue={(currentValue) => {
                  const isEmpty = !currentValue;
                  if (isEmpty) {
                    return "Choose A Practice";
                  }
                  const practiceDisplayName = practices.find(
                    (practice) => practice.wieldyId === currentValue,
                  );
                  return practiceDisplayName?.displayName;
                }}
              >
                {practices.map((practice) => {
                  const { wieldyId, displayName: practiceDisplayName } =
                    practice;
                  return (
                    <MenuItem
                      key={wieldyId}
                      value={wieldyId}
                      data-testid={`practiceSelectItem-${wieldyId}`}
                    >
                      {practiceDisplayName}
                    </MenuItem>
                  );
                })}
              </Select>
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />

      <Controller
        name="type"
        control={control}
        rules={{ required: true }}
        render={({ field: { ref, name, value, onBlur, onChange } }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "start",
            }}
          >
            <FormLabel>
              <InputLabelText>Type</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <Select
                disabled={!selectedPractice}
                name={name}
                value={value}
                label="Choose Type"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                displayEmpty
                IconComponent={KeyboardArrowDownOutlinedIcon}
                inputProps={{
                  "data-testid": "typeInput",
                }}
                MenuProps={{
                  sx: {
                    marginTop: "1rem",
                  },
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  borderRadius: "8px",
                  fontSize: "0.875rem",

                  ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                    {
                      padding: "12px 14px",
                    },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                  fieldset: {
                    legend: {
                      width: 0,
                    },
                  },
                }}
                renderValue={(currentValue) => {
                  const placeholder = "Choose Type";
                  return currentValue || placeholder;
                }}
              >
                <MenuItem
                  key={CREDENTIAL_TYPE.PMS}
                  value={CREDENTIAL_TYPE.PMS}
                  data-testid={`type-${CREDENTIAL_TYPE.PMS.toLowerCase()}`}
                >
                  {CREDENTIAL_TYPE.PMS}
                </MenuItem>
                <MenuItem
                  key={CREDENTIAL_TYPE.PAYER}
                  value={CREDENTIAL_TYPE.PAYER}
                  data-testid={`type-${CREDENTIAL_TYPE.PAYER.toLowerCase()}`}
                >
                  {CREDENTIAL_TYPE.PAYER}
                </MenuItem>
              </Select>
            </InputWrapper>
          </FormControl>
        )}
      />

      {!selectedType && (
        <FormControl
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "start",
          }}
        >
          <FormLabel>
            <InputLabelText>Source</InputLabelText>
          </FormLabel>
          <InputWrapper>
            <Select
              IconComponent={KeyboardArrowDownOutlinedIcon}
              inputProps={{
                "data-testid": "sourceInput",
              }}
              MenuProps={{
                sx: {
                  marginTop: "1rem",
                },
              }}
              sx={{
                backgroundColor: "#F9FAFB",
                width: "100%",
                borderRadius: "8px",
                fontSize: "0.875rem",

                ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                  {
                    padding: "12px 14px",
                  },
                ".MuiOutlinedInput-notchedOutline": {
                  border: "solid 1px rgba(208, 213, 221, 0.5)",
                },
                fieldset: {
                  legend: {
                    width: 0,
                  },
                },
              }}
              disabled
              // Intentionally set as an empty string to trigger placeholder
              defaultValue=" "
              renderValue={() => placeholderSourceText}
            />
          </InputWrapper>
        </FormControl>
      )}

      {selectedType === CREDENTIAL_TYPE.PMS && (
        <Controller
          name="pms"
          control={control}
          rules={{ required: true }}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "start",
              }}
            >
              <FormLabel>
                <InputLabelText>PMS</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <Select
                  name={name}
                  value={value}
                  label="Choose PMS"
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  displayEmpty
                  IconComponent={KeyboardArrowDownOutlinedIcon}
                  inputProps={{
                    "data-testid": "pmsInput",
                  }}
                  MenuProps={{
                    sx: {
                      marginTop: "1rem",
                    },
                  }}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    width: "100%",
                    borderRadius: "8px",
                    fontSize: "0.875rem",

                    ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                      {
                        padding: "12px 14px",
                      },
                    ".MuiOutlinedInput-notchedOutline": {
                      border: "solid 1px rgba(208, 213, 221, 0.5)",
                    },
                    fieldset: {
                      legend: {
                        width: 0,
                      },
                    },
                  }}
                  renderValue={() => {
                    const selectedOne = pmses.find(
                      (pms) => pms.name === selectedPMS,
                    );
                    return selectedOne
                      ? selectedOne.displayLabel
                      : "Choose PMS";
                  }}
                >
                  {hasPMSes &&
                    pmses.map(({ id, name: pmsName, displayLabel }) => {
                      const existingAgents = Array.from(
                        new Set(
                          (userCredentials ?? [])
                            .filter(
                              (c) =>
                                c.pmsDisplayName === displayLabel &&
                                c.practiceId === selectedPractice,
                            )
                            .map((c) => `${c.userType}: ${c.userId}`),
                        ),
                      );

                      return (
                        <MenuItem
                          key={id}
                          value={pmsName}
                          data-testid={`sourceSelectItem-${id}`}
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "1rem",
                          }}
                        >
                          <Box component="span" sx={{ textAlign: "right" }}>
                            {displayLabel}
                          </Box>
                          <Box component="span" sx={{ textAlign: "right" }}>
                            {existingAgents.length > 0 && (
                              <SupportText
                                textOverride={`Added For: ${existingAgents.join(", ")}`}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                </Select>
                <ErrorHelperText message={error?.message} />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}

      {selectedType === CREDENTIAL_TYPE.PAYER && (
        <Controller
          name="source"
          control={control}
          rules={{ required: true }}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "start",
              }}
            >
              <FormLabel>
                <InputLabelText>Source</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <Select
                  name={name}
                  value={value}
                  label="Choose Source"
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  displayEmpty
                  IconComponent={KeyboardArrowDownOutlinedIcon}
                  inputProps={{
                    "data-testid": "sourceSourceInput",
                  }}
                  MenuProps={{
                    sx: {
                      marginTop: "1rem",
                    },
                  }}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    width: "100%",
                    borderRadius: "8px",
                    fontSize: "0.875rem",

                    ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
                      {
                        padding: "12px 14px",
                      },
                    ".MuiOutlinedInput-notchedOutline": {
                      border: "solid 1px rgba(208, 213, 221, 0.5)",
                    },
                    fieldset: {
                      legend: {
                        width: 0,
                      },
                    },
                  }}
                  renderValue={(selectedValue) =>
                    selectedValue === ADD_PAYER_VALUE
                      ? "New Insurance Payer"
                      : selectedSourceObject?.label || placeholderSourceText
                  }
                >
                  {hasPayers &&
                    sources.map(({ id, label }) => {
                      const existingAgents = Array.from(
                        new Set(
                          (userCredentials ?? [])
                            .filter(
                              (c) =>
                                c.sourceId === id &&
                                c.practiceId === selectedPractice,
                            )
                            .map((c) => `${c.userType}: ${c.userId}`),
                        ),
                      );

                      return (
                        <MenuItem
                          key={id}
                          value={id}
                          data-testid={`sourceSelectItem-${id}`}
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "1rem",
                          }}
                        >
                          <Box component="span" sx={{ textAlign: "right" }}>
                            {label}
                          </Box>
                          <Box component="span" sx={{ textAlign: "right" }}>
                            {existingAgents.length > 0 && (
                              <SupportText
                                textOverride={`Added For: ${existingAgents.join(", ")}`}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  <Divider />
                  <MenuItem
                    value={ADD_PAYER_VALUE}
                    data-testid="sourceSelectItem-add-new"
                  >
                    <ListItemIcon>
                      <AddCircleIcon />
                    </ListItemIcon>
                    <ListItemText>Add New</ListItemText>
                  </MenuItem>
                </Select>
                <ErrorHelperText message={error?.message} />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}
      <LineBreak />

      {selectedType === CREDENTIAL_TYPE.PAYER &&
        selectedSource === ADD_PAYER_VALUE && (
          <Controller
            name="unsupportedPayer"
            control={control}
            rules={{ required: true }}
            render={({
              field: { ref, name, value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <FormControl
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "start",
                }}
              >
                <FormLabel>
                  <InputLabelText>Name</InputLabelText>
                </FormLabel>
                <InputWrapper>
                  <TextField
                    name={name}
                    value={value}
                    type="text"
                    variant="outlined"
                    placeholder="Enter Payer Name"
                    ref={ref}
                    onChange={onChange}
                    onBlur={onBlur}
                    inputProps={{
                      "data-testid": "unsupportedPayerInput",
                      autoComplete: "unsupported source",
                      sx: {
                        border: "none",
                      },
                    }}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      width: "100%",
                      fontSize: "1rem",
                      ".MuiInputBase-root": {
                        borderRadius: "8px",
                        border: "none",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        border: "solid 1px rgba(208, 213, 221, 0.5)",
                      },
                    }}
                  />
                  <ErrorHelperText message={error?.message} />
                </InputWrapper>
              </FormControl>
            )}
          />
        )}

      <Controller
        name="username"
        control={control}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <FormLabel>
              <InputLabelText>Username</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                name={name}
                value={value}
                type="text"
                variant="outlined"
                placeholder="Enter Username"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                inputProps={{
                  "data-testid": "usernameInput",
                  autoComplete: "username",
                  sx: {
                    border: "none",
                  },
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  fontSize: "1rem",
                  ".MuiInputBase-root": {
                    borderRadius: "8px",
                    border: "none",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />
      {selectedType === CREDENTIAL_TYPE.PMS &&
        (() => selectedPMS && selectedPMS === "ORTHO2_EDGE")() && (
          <>
            <Controller
              name="activationCode"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <FormLabel>
                    <InputLabelText>Activation Code</InputLabelText>
                  </FormLabel>
                  <InputWrapper>
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      type="text"
                      variant="outlined"
                      placeholder="Enter Activation Code"
                      error={!!error}
                      inputProps={{
                        "data-testid": "activationCodeInput",
                        autoComplete: "off",
                        sx: {
                          border: "none",
                        },
                      }}
                      sx={{
                        backgroundColor: "#F9FAFB",
                        width: "100%",
                        fontSize: "1rem",
                        ".MuiInputBase-root": {
                          borderRadius: "8px",
                          border: "none",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          border: "solid 1px rgba(208, 213, 221, 0.5)",
                        },
                      }}
                    />
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
            <Controller
              name="serialNumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <FormLabel>
                    <InputLabelText>Serial Number</InputLabelText>
                  </FormLabel>
                  <InputWrapper>
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      type="text"
                      variant="outlined"
                      placeholder="Enter Serial Number"
                      error={!!error}
                      inputProps={{
                        "data-testid": "serialNumberInput",
                        autoComplete: "off",
                        sx: {
                          border: "none",
                        },
                      }}
                      sx={{
                        backgroundColor: "#F9FAFB",
                        width: "100%",
                        fontSize: "1rem",
                        ".MuiInputBase-root": {
                          borderRadius: "8px",
                          border: "none",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          border: "solid 1px rgba(208, 213, 221, 0.5)",
                        },
                      }}
                    />
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
            <Controller
              name="customerNumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <FormLabel>
                    <InputLabelText>Customer Number</InputLabelText>
                  </FormLabel>
                  <InputWrapper>
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      type="text"
                      variant="outlined"
                      placeholder="Enter Customer Number"
                      error={!!error}
                      inputProps={{
                        "data-testid": "customerNumberInput",
                        autoComplete: "off",
                        sx: {
                          border: "none",
                        },
                      }}
                      sx={{
                        backgroundColor: "#F9FAFB",
                        width: "100%",
                        fontSize: "1rem",
                        ".MuiInputBase-root": {
                          borderRadius: "8px",
                          border: "none",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          border: "solid 1px rgba(208, 213, 221, 0.5)",
                        },
                      }}
                    />
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
            <Controller
              name="machineIdentifier"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <FormLabel>
                    <InputLabelText>Machine ID</InputLabelText>
                  </FormLabel>
                  <InputWrapper>
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      type="text"
                      variant="outlined"
                      placeholder="Enter Machine ID"
                      error={!!error}
                      inputProps={{
                        "data-testid": "machineIdentifierInput",
                        autoComplete: "off",
                        sx: {
                          border: "none",
                        },
                      }}
                      sx={{
                        backgroundColor: "#F9FAFB",
                        width: "100%",
                        fontSize: "1rem",
                        ".MuiInputBase-root": {
                          borderRadius: "8px",
                          border: "none",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          border: "solid 1px rgba(208, 213, 221, 0.5)",
                        },
                      }}
                    />
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </>
        )}
      <Controller
        name="website"
        control={control}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <FormLabel>
              <InputLabelText>Website</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                name={name}
                disabled={!!selectedSourceObject}
                value={value}
                type="text"
                variant="outlined"
                placeholder={selectedSourceObject?.website || "Enter Website"}
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                inputProps={{
                  "data-testid": "labelInput",
                  autoComplete: "off",
                  sx: {
                    border: "none",
                  },
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  margin: "0 auto",
                  fontSize: "1rem",
                  maxWidth: "785px",
                  ".MuiInputBase-root": {
                    borderRadius: "8px",
                    border: "none",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    opacity: selectedSourceObject?.website ? 1 : "none",
                  },
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />
      {selectedType === CREDENTIAL_TYPE.PAYER && (
        <Controller
          name="notes"
          control={control}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
              }}
            >
              <FormLabel>
                <InputLabelText>Notes</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <TextField
                  multiline
                  name={name}
                  value={value}
                  rows={4}
                  type="text"
                  variant="outlined"
                  placeholder="Enter Notes"
                  ref={ref}
                  onChange={onChange}
                  onBlur={onBlur}
                  inputProps={{
                    "data-testid": "labelInput",
                    autoComplete: "off",
                    sx: {
                      border: "none",
                    },
                  }}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    width: "100%",
                    margin: "0 auto",
                    fontSize: "1rem",
                    maxWidth: "785px",
                    ".MuiInputBase-root": {
                      borderRadius: "8px",
                      border: "none",
                    },
                    ".MuiOutlinedInput-notchedOutline": {
                      border: "solid 1px rgba(208, 213, 221, 0.5)",
                    },
                  }}
                />
                <ErrorHelperText message={error?.message} />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}

      {devMode && (
        <MfaFields
          control={control}
          setValue={setValue}
          existingMfaJson={existingMfaJson}
        />
      )}

      <LineBreak />

      <Controller
        name="password"
        control={control}
        render={({
          field: { ref, name, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <FormLabel>
              <InputLabelText>Password</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <TextField
                name={name}
                value={value}
                type="password"
                variant="outlined"
                placeholder="Enter Password"
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                inputProps={{
                  "data-testid": "passwordInput",
                  autoComplete: "current-password",
                }}
                sx={{
                  backgroundColor: "#F9FAFB",
                  width: "100%",
                  margin: "0 auto",
                  fontSize: "1rem",
                  maxWidth: "785px",
                  ".MuiInputBase-root": {
                    borderRadius: "8px",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "solid 1px rgba(208, 213, 221, 0.5)",
                  },
                }}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />

      <UserFields
        control={control}
        setValue={setValue}
        initialUserType={initialUserType}
        initialUserId={initialUserId}
      />
    </Box>
  );
}
